import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Lock } from 'lucide-react';
import SEO from '../components/SEO';
import { TrustChip } from '../components/TrustChip';

/**
 * D-068 · Founders Wall (review-only stub).
 * -----------------------------------------------------------------
 * Hidden surface — no navigation links, no public references, noindex.
 * Designed to auto-publish when Founder #025 becomes active. Until then,
 * this page is accessible by direct URL only and shows the "sealed"
 * pre-launch state.
 *
 * Activation rule:
 *   activeFounders >= ACTIVATION_THRESHOLD (25) → public mode
 *   Founder override available via prop (for preview).
 *
 * Data source (today): placeholder slots so the founder can ratify the
 * visual; in production the rows come from the membership database
 * (`/api/founders/public`), gated by the founder's opt-in (Doc 26 §3.2).
 */

const ACTIVATION_THRESHOLD = 25;

// Placeholder data — empty by design until Founder #001 signs.
// Each row only carries the public profile information per Doc 26 §3.2:
// D-073 · tier-specific Founder numbering (Gold 300 · Elite 150 · Enterprise 50).
// Numbers are NOT globally unique; each tier has its own roll.
const PUBLIC_FOUNDERS = [
  // Example shape:
  // { number: 1, tier: 'gold', name: 'Aroha Williams', company: 'Example Brand' },
];

// Pre-allocated 25-row scaffold matching the 500-seat tier ratio (60/30/10):
// 15 Gold + 8 Elite + 2 Enterprise.
const TIER_PLAN = [
  ...Array.from({ length: 15 }, (_, i) => ({ tier: 'gold',       tierLabel: 'Gold',       number: i + 1 })),
  ...Array.from({ length: 8 },  (_, i) => ({ tier: 'elite',      tierLabel: 'Elite',      number: i + 1 })),
  ...Array.from({ length: 2 },  (_, i) => ({ tier: 'enterprise', tierLabel: 'Enterprise', number: i + 1 })),
];

const FoundersWall = ({ activeFounders = PUBLIC_FOUNDERS.length, override = false }) => {
  const isPublic = override || activeFounders >= ACTIVATION_THRESHOLD;
  const seatsRemaining = Math.max(0, 500 - activeFounders);

  // Build a 25-row scaffold using TIER_PLAN; a public founder fills the slot
  // when {tier, number} match.
  const rows = TIER_PLAN.map((slot) => {
    const f = PUBLIC_FOUNDERS.find((x) => x.tier === slot.tier && x.number === slot.number);
    return f
      ? { ...slot, name: f.name, company: f.company }
      : { ...slot, name: null, company: null };
  });

  return (
    <div data-testid="founders-wall-page" className="surface-obsidian text-platinum">
      <SEO
        title="Founders Wall — GIWAHS"
        description="The Founding Partners of GIWAHS."
        noindex
      />

      {/* Hero */}
      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1280px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline text-neutral-500">Founders Wall</span>
          <h1
            data-testid="founders-wall-headline"
            className="mt-4 font-display tracking-tighter"
            style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.95 }}
          >
            The founders of GIWAHS.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-400 md:text-lg">
            A permanent record of the organisations that believed first. Founding Partnership
            is worldwide-limited and never reopens — each Founder Member Number is issued once,
            held for the life of the membership, and recognised across the platform. This wall
            is their legacy: the names the industry will remember as the ones who built the
            standard with us.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <TrustChip state="founding-partner" variant="inverse" size="md" founderTier="gold"       founderNumber={1} internal />
            <TrustChip state="founding-partner" variant="inverse" size="md" founderTier="elite"      founderNumber={1} internal />
            <TrustChip state="founding-partner" variant="inverse" size="md" founderTier="enterprise" founderNumber={1} internal />
          </div>
          <p className="mt-4 font-mono-data text-xs text-neutral-500">
            Founder Member #001 — as it will appear across Gold, Elite, and Enterprise tiers.
          </p>
        </div>
      </section>

      {/* Status strip */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1280px] px-5 py-6 md:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              {isPublic ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span
                    data-testid="founders-wall-status"
                    className="font-mono-data text-xs uppercase tracking-[0.16em] text-neutral-400"
                  >
                    Wall is live · {activeFounders} founders active
                  </span>
                </>
              ) : (
                <>
                  <Lock size={14} strokeWidth={1.6} className="text-neutral-700" />
                  <span
                    data-testid="founders-wall-status"
                    className="font-mono-data text-xs uppercase tracking-[0.16em] text-neutral-400"
                  >
                    Sealed · publishes at activation threshold
                  </span>
                </>
              )}
            </div>
            <Link
              to="/founding-partners"
              data-testid="founders-wall-cta-apply"
              className="inline-flex items-center gap-2 border-b border-black pb-1 label-overline hover:gap-3 transition-all"
            >
              Apply for a Founding Partnership <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* The wall */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1280px] px-5 py-12 md:px-8 md:py-20">
          {!isPublic && (
            <div
              data-testid="founders-wall-sealed-banner"
              className="mb-10 border border-neutral-800 bg-black p-6"
            >
              <div className="label-overline text-neutral-500">Sealed preview</div>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                Names appear on this wall only after Founder #025 becomes active and only if each
                Founder has opted in to public visibility under the Founding Partner Terms. Until
                then the wall shows the structure but not the people. Founder Member #001 will be
                the first to occupy a slot.
              </p>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="w-32 py-3 text-left label-overline text-neutral-500">Tier · #</th>
                  <th className="py-3 text-left label-overline text-neutral-500">Founder</th>
                  <th className="hidden py-3 text-left label-overline text-neutral-500 md:table-cell">Company</th>
                  <th className="py-3 text-right label-overline text-neutral-500">Mark</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const filled = !!row.name;
                  const key = `${row.tier}-${row.number}`;
                  return (
                    <tr
                      key={key}
                      data-testid={`founders-wall-row-${key}`}
                      className="border-b border-neutral-900"
                    >
                      <td className="py-4 font-mono-data text-[11px] text-neutral-400">
                        {row.tierLabel} · #{String(row.number).padStart(3, '0')}
                      </td>
                      <td className="py-4 text-sm">
                        {filled
                          ? <span className="text-neutral-100">{row.name}</span>
                          : <span className="text-neutral-700">— reserved —</span>}
                      </td>
                      <td className="hidden py-4 text-sm text-neutral-400 md:table-cell">
                        {filled ? row.company : <span className="text-neutral-700">—</span>}
                      </td>
                      <td className="py-4 text-right">
                        {filled
                          ? <TrustChip state="founding-partner" variant="inverse" founderTier={row.tier} founderNumber={row.number} internal />
                          : <span className="font-mono-data text-[10px] uppercase tracking-[0.18em] text-neutral-700">unfilled</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex flex-col items-start justify-between gap-4 border-t border-neutral-800 pt-6 md:flex-row md:items-center">
            <span className="font-mono-data text-xs text-neutral-500">
              Showing slots 1–{ACTIVATION_THRESHOLD}. Slots #026–#500 reveal as additional waves complete.
            </span>
            <Link
              to="/press"
              className="inline-flex items-center gap-1 label-overline text-neutral-300 hover:text-white"
            >
              Brand assets <ArrowUpRight size={12} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FoundersWall;
